import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Text } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import "./ImageCropModal.css";

/**
 * ImageCropModal
 *
 * Opens after an image is chosen. Shows the full image with a visible
 * crop-frame overlaid. The user drags the FRAME over the image to decide
 * what region is shown. On confirm it returns an objectPosition { x, y }
 * (percentages) that CSS `object-fit: cover` / `object-position` uses.
 *
 * Props:
 *   src          – data-URL or remote URL of the uploaded image
 *   aspectRatio  – "banner" (16/9) | "prize" (1/1)
 *   initialPos   – { x: number, y: number } in percent (default 50,50)
 *   onConfirm    – (position: { x, y }) => void
 *   onCancel     – () => void
 */
function ImageCropModal({ src, aspectRatio, initialPos, onConfirm, onCancel }) {
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  const MIN_FRAME = 60; // px minimum frame dimension

  // Frame position (top-left) and size – separate so resize doesn't reset position
  const [framePos, setFramePos] = useState({ left: 0, top: 0 });
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  // Image pixel dimensions (set once after layout)
  const [imgDims, setImgDims] = useState(null); // { imgW, imgH }

  const frameAspect = aspectRatio === "banner" ? 16 / 9 : 1;

  // Once the image is rendered compute everything.
  // Initial frame = full image width (constrained by height).
  const computeDims = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const imgEl = container.querySelector(".icm-image");
    if (!imgEl) return;

    const imgRect = imgEl.getBoundingClientRect();
    const imgW = imgRect.width;
    const imgH = imgRect.height;
    if (imgW === 0 || imgH === 0) return;

    // Start at full image width; if that makes the frame taller than the
    // image, shrink from height instead.
    let frameW = imgW;
    let frameH = frameW / frameAspect;
    if (frameH > imgH) {
      frameH = imgH;
      frameW = frameH * frameAspect;
    }

    // Center the frame
    const left = (imgW - frameW) / 2;
    const top = (imgH - frameH) / 2;

    setImgDims({ imgW, imgH });
    setFrameSize({ width: frameW, height: frameH });
    setFramePos({ left, top });
  }, [frameAspect]);

  useEffect(() => {
    // Give the browser a tick to layout the image
    const t = setTimeout(computeDims, 80);
    return () => clearTimeout(t);
  }, [src, computeDims]);

  // Recompute on window resize
  useEffect(() => {
    window.addEventListener("resize", computeDims);
    return () => window.removeEventListener("resize", computeDims);
  }, [computeDims]);

  /* ── Clamp frame position within image bounds ── */
  const clampPos = useCallback(
    (left, top, fw, fh) => {
      if (!imgDims) return { left, top };
      return {
        left: Math.max(0, Math.min(left, imgDims.imgW - fw)),
        top: Math.max(0, Math.min(top, imgDims.imgH - fh)),
      };
    },
    [imgDims]
  );

  /* ── Drag (move frame) ── */
  const onMouseDown = useCallback(
    e => {
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startLeft: framePos.left,
        startTop: framePos.top,
      };

      const onMove = moveE => {
        if (!dragRef.current) return;
        const dx = moveE.clientX - dragRef.current.startX;
        const dy = moveE.clientY - dragRef.current.startY;
        setFramePos(
          clampPos(
            dragRef.current.startLeft + dx,
            dragRef.current.startTop + dy,
            frameSize.width,
            frameSize.height
          )
        );
      };
      const onUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [framePos, frameSize, clampPos]
  );

  const onTouchStart = useCallback(
    e => {
      const t = e.touches[0];
      dragRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        startLeft: framePos.left,
        startTop: framePos.top,
      };

      const onMove = moveE => {
        moveE.preventDefault();
        if (!dragRef.current) return;
        const touch = moveE.touches[0];
        const dx = touch.clientX - dragRef.current.startX;
        const dy = touch.clientY - dragRef.current.startY;
        setFramePos(
          clampPos(
            dragRef.current.startLeft + dx,
            dragRef.current.startTop + dy,
            frameSize.width,
            frameSize.height
          )
        );
      };
      const onEnd = () => {
        dragRef.current = null;
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
      };
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onEnd);
    },
    [framePos, frameSize, clampPos]
  );

  /* ── Resize (bottom-right handle, maintains aspect ratio) ── */
  const onResizeMouseDown = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        startX: e.clientX,
        startW: frameSize.width,
      };

      const onMove = moveE => {
        if (!resizeRef.current || !imgDims) return;
        const dx = moveE.clientX - resizeRef.current.startX;
        let newW = Math.max(MIN_FRAME, resizeRef.current.startW + dx);
        // Clamp so frame stays within image from current left edge
        newW = Math.min(newW, imgDims.imgW - framePos.left);
        let newH = newW / frameAspect;
        // Also clamp height
        if (framePos.top + newH > imgDims.imgH) {
          newH = imgDims.imgH - framePos.top;
          newW = newH * frameAspect;
        }
        newW = Math.max(MIN_FRAME, newW);
        newH = newW / frameAspect;
        setFrameSize({ width: newW, height: newH });
      };
      const onUp = () => {
        resizeRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [frameSize, framePos, imgDims, frameAspect]
  );

  const onResizeTouchStart = useCallback(
    e => {
      e.stopPropagation();
      const t = e.touches[0];
      resizeRef.current = {
        startX: t.clientX,
        startW: frameSize.width,
      };

      const onMove = moveE => {
        moveE.preventDefault();
        if (!resizeRef.current || !imgDims) return;
        const touch = moveE.touches[0];
        const dx = touch.clientX - resizeRef.current.startX;
        let newW = Math.max(MIN_FRAME, resizeRef.current.startW + dx);
        newW = Math.min(newW, imgDims.imgW - framePos.left);
        let newH = newW / frameAspect;
        if (framePos.top + newH > imgDims.imgH) {
          newH = imgDims.imgH - framePos.top;
          newW = newH * frameAspect;
        }
        newW = Math.max(MIN_FRAME, newW);
        newH = newW / frameAspect;
        setFrameSize({ width: newW, height: newH });
      };
      const onEnd = () => {
        resizeRef.current = null;
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
      };
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onEnd);
    },
    [frameSize, framePos, imgDims, frameAspect]
  );

  const handleConfirm = () => {
    if (!imgDims) {
      onConfirm({ x: 50, y: 50 });
      return;
    }
    const rangeX = imgDims.imgW - frameSize.width;
    const rangeY = imgDims.imgH - frameSize.height;
    const x = rangeX <= 0 ? 50 : (framePos.left / rangeX) * 100;
    const y = rangeY <= 0 ? 50 : (framePos.top / rangeY) * 100;
    onConfirm({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  };

  if (!src) return null;

  return (
    <div
      className="icm-overlay"
      onClick={e => e.target === e.currentTarget && onCancel()}
    >
      <div className="icm-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="icm-header">
          <div className="icm-header-text">
            <Text fw={700} size="md">
              Position Image
            </Text>
            <Text size="xs" c="dimmed">
              Drag the frame to reposition · Drag the corner handle to resize
            </Text>
          </div>
          <button className="icm-close-btn" onClick={onCancel}>
            <IconX size={18} />
          </button>
        </div>

        {/* Image + frame */}
        <div className="icm-canvas-wrap">
          <div className="icm-canvas" ref={containerRef}>
            {/* The full image */}
            <img
              src={src}
              alt="Crop preview"
              className="icm-image"
              draggable={false}
              onLoad={computeDims}
            />

            {/* Dark vignette outside frame */}
            {imgDims && (
              <>
                {/* top */}
                <div
                  className="icm-shade"
                  style={{ top: 0, left: 0, right: 0, height: framePos.top }}
                />
                {/* bottom */}
                <div
                  className="icm-shade"
                  style={{
                    top: framePos.top + frameSize.height,
                    left: 0,
                    right: 0,
                    height: Math.max(
                      0,
                      imgDims.imgH - framePos.top - frameSize.height
                    ),
                  }}
                />
                {/* left */}
                <div
                  className="icm-shade"
                  style={{
                    top: framePos.top,
                    left: 0,
                    width: framePos.left,
                    height: frameSize.height,
                  }}
                />
                {/* right */}
                <div
                  className="icm-shade"
                  style={{
                    top: framePos.top,
                    left: framePos.left + frameSize.width,
                    right: 0,
                    height: frameSize.height,
                  }}
                />

                {/* The draggable frame */}
                <div
                  ref={frameRef}
                  className="icm-frame"
                  style={{
                    left: framePos.left,
                    top: framePos.top,
                    width: frameSize.width,
                    height: frameSize.height,
                  }}
                  onMouseDown={onMouseDown}
                  onTouchStart={onTouchStart}
                >
                  {/* Corner display handles (tl, tr, bl) */}
                  <div className="icm-corner icm-tl" />
                  <div className="icm-corner icm-tr" />
                  <div className="icm-corner icm-bl" />
                  {/* Bottom-right: resize handle */}
                  <div
                    className="icm-corner icm-br icm-resize-handle"
                    onMouseDown={onResizeMouseDown}
                    onTouchStart={onResizeTouchStart}
                  />
                  <div className="icm-frame-hint">↕↔ Drag to reposition</div>
                </div>
              </>
            )}

            {/* Loading state */}
            {!imgDims && <div className="icm-loading">Loading preview…</div>}
          </div>
        </div>

        {/* Footer */}
        <div className="icm-footer">
          <Text size="xs" c="dimmed" style={{ flex: 1 }}>
            {aspectRatio === "banner"
              ? "Banner frame (16:9)"
              : "Prize image frame (1:1)"}
          </Text>
          <Button
            variant="default"
            size="sm"
            onClick={onCancel}
            leftSection={<IconX size={14} />}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            leftSection={<IconCheck size={14} />}
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
            }}
          >
            Use This
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropModal;
